/**
 * Module dependencies.
 */

import { cn } from 'app/lib/utils';
import { X } from 'lucide-react';
import React from 'react';
import { badgeBackground, badgeForeground } from './badge-utils';

/**
 * `Props` type.
 */

type Props = {
  children: React.ReactNode;
  className?: string;
  color: string;
  onRemove?: () => void;
  removeLabel?: string;
};

/**
 * Export `BadgePill` — a colored label pill styled like the system badges: a
 * light tint of the color with same-hue darker text. Matches their dimensions
 * (`px-2 py-0.5 text-[11px]`) and adds an optional remove control.
 */

export function BadgePill({ children, className, color, onRemove, removeLabel }: Props): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex max-w-[180px] items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-medium',
        className
      )}
      style={{ backgroundColor: badgeBackground(color), color: badgeForeground(color) }}
    >
      <span className={'truncate'}>{children}</span>

      {onRemove && (
        <button
          aria-label={removeLabel}
          className={'-mr-0.5 inline-flex shrink-0 items-center opacity-70 transition-opacity hover:opacity-100'}
          onClick={event => {
            event.stopPropagation();
            onRemove();
          }}
          title={removeLabel}
          type={'button'}
        >
          <X className={'h-3 w-3 shrink-0'} />
        </button>
      )}
    </span>
  );
}
